import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOffIcon from '@mui/icons-material/PersonOffOutlined';
import { toast } from 'react-toastify';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
  ActionMenu,
  ConfirmDialog,
} from '../../components/ui';
import type { Column, ActionItem } from '../../components/ui';
import { usersApi } from '../../api/users';
import { PAGE_SIZE } from '../../constants';
import { formatDate, paginate, matchesSearch, initials } from '../../utils';
import type { AccountStatus, Customer } from '../../types';

type ConfirmAction = {
  id: string;
  name: string;
  status: AccountStatus;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
};

const STATUS_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Active', value: 'Active' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'Deactivated', value: 'Deactivated' },
];

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('registrationDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    usersApi
      .getUsers({ page: 1, limit: 1000 })
      .then((res) => setCustomers(res.users))
      .catch(() => {
        toast.error('Failed to load users');
        setCustomers([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let rows = customers.filter((c) => {
      const q = search.trim();
      const matchesQ =
        !q || matchesSearch(c.name, q) || matchesSearch(c.email, q);
      const matchesStatus = status === 'All' || c.status === status;
      return matchesQ && matchesStatus;
    });

    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortKey === 'registrationDate') {
        cmp =
          new Date(a.registrationDate).getTime() -
          new Date(b.registrationDate).getTime();
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [customers, search, status, sortKey, sortDir]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const pageRows = useMemo(
    () => paginate(filtered, page, PAGE_SIZE),
    [filtered, page]
  );

  const handleSort = (columnId: string) => {
    if (sortKey === columnId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(columnId);
      setSortDir('asc');
    }
  };

  const openStatusConfirm = (
    customer: Customer,
    nextStatus: AccountStatus
  ) => {
    const map: Record<
      AccountStatus,
      Omit<ConfirmAction, 'id' | 'name' | 'status'>
    > = {
      Suspended: {
        title: 'Suspend user',
        message: `Suspend ${customer.name}? They will temporarily lose access.`,
        confirmLabel: 'Suspend',
        danger: true,
      },
      Active: {
        title: 'Reactivate user',
        message: `Reactivate ${customer.name}? They will regain full access.`,
        confirmLabel: 'Reactivate',
      },
      Deactivated: {
        title: 'Deactivate user',
        message: `Deactivate ${customer.name}? This disables the account.`,
        confirmLabel: 'Deactivate',
        danger: true,
      },
      Inactive: {
        title: 'Update status',
        message: `Set ${customer.name} to Inactive?`,
        confirmLabel: 'Confirm',
      },
    };
    const cfg = map[nextStatus];
    setConfirm({
      id: customer.id,
      name: customer.name,
      status: nextStatus,
      ...cfg,
    });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      // Status update endpoint is not available on admin user APIs yet.
      toast.info('User status update API is not available yet');
      setConfirm(null);
    } catch {
      toast.error('Failed to update user status');
    } finally {
      setConfirmLoading(false);
    }
  };

  const getActions = (row: Customer): ActionItem[] => [
    {
      label: 'View',
      icon: <VisibilityIcon fontSize="small" />,
      onClick: () => navigate(`/users/${row.id}`),
    },
    {
      label: 'Suspend',
      icon: <BlockIcon fontSize="small" />,
      onClick: () => openStatusConfirm(row, 'Suspended'),
      disabled: row.status === 'Suspended' || row.status === 'Deactivated',
      danger: true,
    },
    {
      label: 'Reactivate',
      icon: <CheckCircleIcon fontSize="small" />,
      onClick: () => openStatusConfirm(row, 'Active'),
      disabled: row.status === 'Active',
    },
    {
      label: 'Deactivate',
      icon: <PersonOffIcon fontSize="small" />,
      onClick: () => openStatusConfirm(row, 'Deactivated'),
      disabled: row.status === 'Deactivated',
      danger: true,
    },
  ];

  const columns: Column<Customer>[] = [
    {
      id: 'id',
      label: 'ID',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      minWidth: 180,
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            src={row.avatar}
            sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}
          >
            {initials(row.name)}
          </Avatar>
          <Typography variant="body2" fontWeight={600}>
            {row.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 180,
      render: (row) => row.email,
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 140,
      render: (row) => row.phone,
    },
    {
      id: 'registrationDate',
      label: 'Registration Date',
      minWidth: 140,
      sortable: true,
      render: (row) => formatDate(row.registrationDate),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 110,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      minWidth: 80,
      render: (row) => <ActionMenu actions={getActions(row)} />,
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Manage customer accounts and access"
      />

      <PageFilters
        search={search}
        searchPlaceholder="Search by name or email…"
        onSearchChange={setSearch}
        filters={[
          {
            id: 'status',
            label: 'Status',
            value: status,
            options: STATUS_OPTIONS,
            onChange: setStatus,
          },
        ]}
      />

      <DataTable
        columns={columns}
        rows={pageRows}
        loading={loading}
        emptyTitle="No users found"
        emptyDescription="Try a different search or status filter."
        onSort={handleSort}
        sortKey={sortKey}
        sortDir={sortDir}
        getRowId={(row) => row.id}
      />

      {!loading && filtered.length > 0 && (
        <TablePaginationBar
          page={page}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onChange={setPage}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => !confirmLoading && setConfirm(null)}
      />
    </Box>
  );
};

export default UsersPage;
